import { useEffect, useRef, useState } from 'react';

/**
 * Калкулатор (13.10.2026) — плаващ прозорец над всеки екран.
 *
 * Отваря се от бутона в менюто или с F9 отвсякъде. Докато е на фокус,
 * клавиатурата пише в него: цифри, + - * /, запетая или точка, Enter
 * или = за резултат, Backspace, Delete/C за изчистване, Esc затваря.
 * Щракне ли се другаде, спира да слуша — не краде писането в полетата
 * на продажбата.
 *
 * Сметката е израз с приоритет (2+3*4 = 14), не касов апарат, който
 * смята отляво надясно. Изчислява се със собствен разбор, без eval().
 */

const OP = { '+': 1, '-': 1, '*': 2, '/': 2 };

function smetni(izraz) {
  const tokens = [];
  let num = '';
  for (const ch of izraz.replace(/,/g, '.')) {
    if (/[0-9.]/.test(ch)) { num += ch; continue; }
    if (num) { tokens.push(num); num = ''; }
    if (ch === '-' && (tokens.length === 0 || OP[tokens[tokens.length - 1]])) { num = '-'; continue; }
    if (OP[ch]) tokens.push(ch);
  }
  if (num) tokens.push(num);
  while (tokens.length && OP[tokens[tokens.length - 1]]) tokens.pop();
  if (!tokens.length) return null;

  const out = [];
  const ops = [];
  const apply = () => {
    const op = ops.pop();
    const b = out.pop();
    const a = out.pop();
    out.push(op === '+' ? a + b : op === '-' ? a - b : op === '*' ? a * b : a / b);
  };
  for (const t of tokens) {
    if (OP[t]) {
      while (ops.length && OP[ops[ops.length - 1]] >= OP[t]) apply();
      ops.push(t);
    } else {
      const n = Number(t);
      if (!Number.isFinite(n)) return null;
      out.push(n);
    }
  }
  while (ops.length) apply();
  const r = out[0];
  return Number.isFinite(r) ? Math.round(r * 1e10) / 1e10 : null;
}

const pokazhi = (n) => (n == null ? '' : String(n).replace('.', ','));
const ZNAK = { '*': '×', '/': '÷', '-': '−' };
const krasiv = (s) => s.replace(/[*/-]/g, (c) => ZNAK[c]).replace(/\./g, ',');

export default function Calculator({ open, onClose }) {
  const [izraz, setIzraz] = useState('');
  const [rezultat, setRezultat] = useState(null);
  const [istoria, setIstoria] = useState([]);
  const [kopirano, setKopirano] = useState(false);
  const ref = useRef(null);

  useEffect(() => { if (open) ref.current?.focus(); }, [open]);

  if (!open) return null;

  const tekushto = smetni(izraz);

  function dobavi(ch) {
    setIzraz((cur) => {
      // След „=" нова цифра започва нова сметка, операция продължава резултата.
      let base = cur;
      if (rezultat != null) {
        base = OP[ch] ? String(rezultat) : '';
        setRezultat(null);
      }
      if (OP[ch] && ch !== '-' && base === '') return base;
      // Две операции една след друга — последната печели (освен минус за знак).
      if (OP[ch] && OP[base.slice(-1)] && !(ch === '-' && base.slice(-1) !== '-')) {
        return base.slice(0, -1) + ch;
      }
      if (ch === '.') {
        const posledno = base.split(/[+\-*/]/).pop();
        if (posledno.includes('.')) return base;
        if (posledno === '') return `${base}0.`;
      }
      return base + ch;
    });
  }

  function ravno() {
    const r = smetni(izraz);
    if (r == null) return;
    setIstoria((h) => [`${krasiv(izraz)} = ${pokazhi(r)}`, ...h].slice(0, 5));
    setRezultat(r);
    setIzraz(String(r));
  }

  function iztrii() { setRezultat(null); setIzraz((s) => s.slice(0, -1)); }
  function izchisti() { setRezultat(null); setIzraz(''); }

  // ДДС 20% — върху целия текущ израз.
  function dds(mnozhitel) {
    const r = smetni(izraz);
    if (r == null) return;
    const n = Math.round(r * mnozhitel * 100) / 100;
    setIstoria((h) => [`${pokazhi(r)} ${mnozhitel > 1 ? '+' : '−'} ДДС = ${pokazhi(n)}`, ...h].slice(0, 5));
    setRezultat(n);
    setIzraz(String(n));
  }

  function procent() {
    // „200+10%" → 220; самотно „15%" → 0,15.
    const m = izraz.match(/^(.*?)([+\-*/])(\d*\.?\d+)$/);
    if (m) {
      const baza = smetni(m[1]);
      if (baza == null) return;
      const p = Number(m[3]);
      const stoinost = m[2] === '+' || m[2] === '-' ? baza * p / 100 : p / 100;
      setIzraz(`${m[1]}${m[2]}${Math.round(stoinost * 1e10) / 1e10}`);
    } else {
      const r = smetni(izraz);
      if (r != null) setIzraz(String(r / 100));
    }
  }

  function kopirai() {
    const v = rezultat ?? tekushto;
    if (v == null) return;
    navigator.clipboard?.writeText(pokazhi(v)).then(() => {
      setKopirano(true);
      setTimeout(() => setKopirano(false), 1200);
    }, () => {});
  }

  function onKeyDown(e) {
    const k = e.key;
    if (e.ctrlKey || e.metaKey || e.altKey) {
      if ((e.ctrlKey || e.metaKey) && k.toLowerCase() === 'c') { e.preventDefault(); kopirai(); }
      return;
    }
    if (/^[0-9]$/.test(k)) dobavi(k);
    else if (k === ',' || k === '.' || k === 'Decimal') dobavi('.');
    else if (OP[k]) dobavi(k);
    else if (k === 'Enter' || k === '=') ravno();
    else if (k === 'Backspace') iztrii();
    else if (k === 'Delete' || k === 'c' || k === 'C' || k === 'с' || k === 'С') izchisti();
    else if (k === '%') procent();
    else if (k === 'Escape') onClose();
    else return;
    e.preventDefault();
    e.stopPropagation();
  }

  const btn = (label, onClick, cls = '') => (
    <button type="button" className={`calc-btn ${cls}`} onClick={() => { onClick(); ref.current?.focus(); }}>
      {label}
    </button>
  );

  return (
    <div className="calc" ref={ref} tabIndex={-1} onKeyDown={onKeyDown} role="dialog" aria-label="Калкулатор">
      <div className="calc-head">
        <strong>Калкулатор</strong>
        <span className="muted" style={{ fontSize: 11 }}>F9 · Esc затваря</span>
        <span className="spacer" />
        <button type="button" className="link" onClick={onClose} aria-label="Затвори">✕</button>
      </div>
      <div className="calc-screen">
        <div className="calc-expr">{krasiv(izraz) || '0'}</div>
        <div className="calc-result">
          {pokazhi(rezultat ?? tekushto) || '0'}
        </div>
      </div>
      <div className="calc-grid">
        {btn('C', izchisti, 'fn')}
        {btn('⌫', iztrii, 'fn')}
        {btn('%', procent, 'fn')}
        {btn('÷', () => dobavi('/'), 'op')}
        {btn('7', () => dobavi('7'))}
        {btn('8', () => dobavi('8'))}
        {btn('9', () => dobavi('9'))}
        {btn('×', () => dobavi('*'), 'op')}
        {btn('4', () => dobavi('4'))}
        {btn('5', () => dobavi('5'))}
        {btn('6', () => dobavi('6'))}
        {btn('−', () => dobavi('-'), 'op')}
        {btn('1', () => dobavi('1'))}
        {btn('2', () => dobavi('2'))}
        {btn('3', () => dobavi('3'))}
        {btn('+', () => dobavi('+'), 'op')}
        {btn('0', () => dobavi('0'), 'wide')}
        {btn(',', () => dobavi('.'))}
        {btn('=', ravno, 'eq')}
        {btn('+ДДС', () => dds(1.2), 'fn')}
        {btn('−ДДС', () => dds(1 / 1.2), 'fn')}
        {btn(kopirano ? '✓' : 'копирай', kopirai, 'fn wide')}
      </div>
      {istoria.length > 0 && (
        <div className="calc-history">
          {istoria.map((h, i) => <div key={i}>{h}</div>)}
        </div>
      )}
    </div>
  );
}
